/**
 * Process endpoint values with proper type conversion
 * @param {Object} endpointValues - The raw endpoint values from the form
 * @param {Object} moduleConfig - The imported module configuration
 * @returns {Object} - Processed endpoint values with status and validation results
 */
export const processEndpointValues = (endpointValues, moduleConfig) => {
  // Create a deep copy of the input to avoid modifying the original
  const processedEndpoints = JSON.parse(JSON.stringify(endpointValues));
  
  // Add validation status tracking
  const validationResults = {
    isValid: true,
    endpoints: {}
  };
  
  // Process each endpoint
  Object.keys(processedEndpoints).forEach(endpointKey => {
    const endpointObj = processedEndpoints[endpointKey];
    const endpointIndex = parseInt(endpointKey.split('_')[1]);
    const endpointConfig = moduleConfig.moduleConfig.endpoints[endpointIndex];
    
    if (!endpointConfig) {
      validationResults.endpoints[endpointKey] = {
        isValid: false,
        errors: [`Configuration for ${endpointKey} not found`]
      };
      return;
    }

    // Initialize validation for this endpoint
    validationResults.endpoints[endpointKey] = {
      isValid: true,
      errors: [],
      paramErrors: {}
    };

    // Create a new params object to rebuild with correct types
    const processedParams = {};
    
    // Track parent types for params that need to be processed later
    const parentTypes = {};
    
    // Check for required parameters that might be missing
    Object.entries(endpointConfig.parameters).forEach(([paramName, paramConfig]) => {
      // Store parentType information if it exists
      if (paramConfig.parentType) {
        const parentName = paramName.split('.')[0];
        parentTypes[parentName] = paramConfig.parentType;
      }
      
      if (paramConfig.required && 
          (!endpointObj.params.hasOwnProperty(paramName) || 
           endpointObj.params[paramName] === '' || 
           endpointObj.params[paramName] === null || 
           endpointObj.params[paramName] === undefined)) {
        
        validationResults.isValid = false;
        validationResults.endpoints[endpointKey].isValid = false;
        validationResults.endpoints[endpointKey].paramErrors[paramName] = `Required parameter is missing`;
      }
    });
    
    // Process each parameter
    Object.entries(endpointObj.params).forEach(([paramName, paramValue]) => {
      // Trim string values
      let trimmedValue = paramValue;
      if (typeof paramValue === 'string') {
        trimmedValue = paramValue.trim();
      }
      
      // Skip empty values (after trimming) - note that 0 is NOT considered empty
      if (trimmedValue === '' || trimmedValue === null || trimmedValue === undefined) {
        // Check if this is a required parameter
        const paramConfig = endpointConfig.parameters[paramName];
        if (paramConfig && paramConfig.required) {
          validationResults.isValid = false;
          validationResults.endpoints[endpointKey].isValid = false;
          validationResults.endpoints[endpointKey].paramErrors[paramName] = `Required parameter is empty`;
        }
        return;
      }
      
      // Get parameter configuration
      const paramConfig = endpointConfig.parameters[paramName];
      if (!paramConfig) {
        validationResults.endpoints[endpointKey].errors.push(`Configuration for parameter ${paramName} not found`);
        return;
      }
      
      try {
        // Convert value to the correct type
        const typedValue = convertToType(trimmedValue, paramConfig.type);
        
        // Store as flat structure with original parameter name
        processedParams[paramName] = typedValue;
      } catch (error) {
        // Handle type conversion errors
        validationResults.isValid = false;
        validationResults.endpoints[endpointKey].isValid = false;
        validationResults.endpoints[endpointKey].paramErrors[paramName] = `Type conversion error: ${error.message}`;
      }
    });
    
    // Replace the endpoint's params with the processed ones
    endpointObj.params = processedParams;
    
    // Add parent type information to the endpoint
    for (const [parentName, parentType] of Object.entries(parentTypes)) {
      endpointObj[`paramType_${parentName}`] = parentType;
    }
  });
  
  return {
    endpoints: processedEndpoints,
    validation: validationResults
  };
}

/**
 * Convert a value to the specified type
 * @param {any} value - The value to convert
 * @param {string} type - The target type (string, integer, boolean, etc.)
 * @returns {any} - The converted value
 * @throws {Error} - If conversion fails
 */
export const convertToType = (value, type) => {
  if (value === '' || value === null || value === undefined) {
    return undefined;
  }
  
  // Special case for zero values
  if (value === 0 || value === '0') {
    if (type === 'integer' || type === 'number') {
      return 0;
    }
  }
  
  switch (type) {
    case 'integer':
      const intValue = parseInt(value, 10);
      if (isNaN(intValue)) {
        throw new Error(`Cannot convert "${value}" to integer`);
      }
      return intValue;
    
    case 'number':
      const floatValue = parseFloat(value);
      if (isNaN(floatValue)) {
        throw new Error(`Cannot convert "${value}" to number`);
      }
      return floatValue;
    
    case 'boolean':
      // Handle various boolean representations
      if (typeof value === 'string') {
        const lowered = value.toLowerCase();
        if (lowered === 'true' || lowered === 'yes' || lowered === '1') {
          return true;
        }
        if (lowered === 'false' || lowered === 'no' || lowered === '0') {
          return false;
        }
        throw new Error(`Cannot convert "${value}" to boolean`);
      }
      return Boolean(value);
    
    case 'object':
      // If it's a string, try to parse it as JSON
      if (typeof value === 'string') {
        try {
          return JSON.parse(value);
        } catch (e) {
          throw new Error(`Cannot parse "${value}" as JSON object`);
        }
      }
      return value;
    
    case 'array':
      // If it's a string, try to parse it as JSON
      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value);
          return Array.isArray(parsed) ? parsed : [parsed];
        } catch (e) {
          // If parsing fails, treat as comma-separated values
          return value.split(',').map(item => item.trim());
        }
      }
      // If already an array, return as is
      return Array.isArray(value) ? value : [value];
    
    case 'string':
    default:
      // Convert to string if it's not already
      return typeof value === 'string' ? value : String(value);
  }
}



/**
 * Restructures flat parameters with dot notation into nested objects
 */
export const restructureObject = (moduleNodes) => {
  // Create a deep copy to avoid modifying the original
  const result = JSON.parse(JSON.stringify(moduleNodes));
  
  // Process each node
  Object.keys(result).forEach(nodeId => {
    const nodeObj = result[nodeId];
    
    // Find all endpoint keys (they start with "endpoint_")
    const endpointKeys = Object.keys(nodeObj).filter(key => key.startsWith('endpoint_'));
    
    // Process each endpoint
    endpointKeys.forEach(endpointKey => {
      const endpointObj = nodeObj[endpointKey];
      const flatParams = endpointObj.params;
      const nestedParams = {};
      
      // Skip if no params
      if (!flatParams) return;
      
      // Find all paramType properties in the endpoint
      const paramTypes = {};
      Object.keys(endpointObj).forEach(key => {
        if (key.startsWith('paramType_')) {
          const parentName = key.substring('paramType_'.length);
          paramTypes[parentName] = endpointObj[key];
        }
      });
      
      // Restructure each parameter
      Object.entries(flatParams).forEach(([paramName, paramValue]) => {
        // Skip null or undefined values
        if (paramValue === null || paramValue === undefined) {
          return;
        }
        
        // If the parameter contains a period, it needs to be nested
        if (paramName.includes('.')) {
          const parts = paramName.split('.');
          const parentKey = parts[0];
          const childKey = parts[1];
          
          // Get the parent type
          const parentType = paramTypes[parentKey] || 'object';
          
          // Initialize parent object if it doesn't exist
          if (!nestedParams[parentKey]) {
            // Initialize based on parentType
            if (parentType === 'array') {
              nestedParams[parentKey] = [];
            } else {
              nestedParams[parentKey] = {};
            }
          }
          
          // Add to parent object/array
          if (parentType === 'array') {
            nestedParams[parentKey].push(paramValue);
          } else {
            nestedParams[parentKey][childKey] = paramValue;
          }
        } else {
          // Not nested, just add directly
          nestedParams[paramName] = paramValue;
        }
      });
      
      // Replace flat params with nested ones
      endpointObj.params = nestedParams;
      
      // Remove paramType_ properties as they're no longer needed
      Object.keys(endpointObj).forEach(key => {
        if (key.startsWith('paramType_')) {
          delete endpointObj[key];
        }
      });
    });
  });
  
  return result;
}


/**
 * Processes API endpoints by replacing {paramName} patterns with corresponding parameter values.
 * 
 * @param {Object} inputObject - The input object containing endpoints
 * @return {Object} - The processed object with replacements applied
 */
export const replaceUrlVariable = (inputObject) => {
  // Create a deep copy of the input object to avoid modifying the original
  const processedObject = JSON.parse(JSON.stringify(inputObject));
  
  // Iterate over each ID in the object
  for (const id in processedObject) {
    const idObject = processedObject[id];
    
    // Find all keys that start with 'endpoint_'
    const endpointKeys = Object.keys(idObject).filter(key => key.startsWith('endpoint_'));
    
    // Process each endpoint
    for (const endpointKey of endpointKeys) {
      const endpoint = idObject[endpointKey];
      
      // Check if this endpoint has params
      if (endpoint.params) {
        // Helper function to process values recursively
        function processValue(value) {
          if (typeof value === 'string') {
            // Replace patterns like {ParamName} in the string
            return value.replace(/{([^{}]+)}/g, (match, paramName) => {
              // Check if this parameter exists in the params object
              if (paramName in endpoint.params) {
                const paramValue = endpoint.params[paramName];
                
                // Remove the key:value pair from params
                delete endpoint.params[paramName];
                
                // Return the parameter value
                return paramValue;
              }
              
              // If no match found, return the original match
              return match;
            });
          } else if (Array.isArray(value)) {
            // Process each item in the array
            return value.map(item => processValue(item));
          } else if (typeof value === 'object' && value !== null) {
            // Skip processing the params object itself
            if (value === endpoint.params) return value;
            
            // Process each property in the object
            const result = {};
            for (const key in value) {
              result[key] = processValue(value[key]);
            }
            return result;
          }
          
          // Return non-string, non-object values as is
          return value;
        }
        
        // Process all properties in the endpoint except params
        for (const key in endpoint) {
          if (key !== 'params') {
            endpoint[key] = processValue(endpoint[key]);
          }
        }
      }
    }
  }
  
  return processedObject;
}